import { toQuery, type HttpClient } from '../client';
import type { UserProfile } from '../types/auth';

export type BindQrType =
  | 'ORG'
  | 'DEPT'
  | 'LANDMARK'
  | 'BUILDING'
  | 'FLOOR'
  | 'WAREHOUSE'
  | 'DELIVERY'
  | 'MATERIAL'
  | 'STOCKTAKE';

export interface BindQrCreateRequest {
  qrType?: BindQrType;
  targetId?: string;
  bindRoleCode?: string;
  expireMinutes?: number;
  maxUse?: number;
  needAudit?: boolean;
  device?: string;
}

export interface BindQrCreateResult {
  scene: string;
  /** 二维码内容；落地页未配置时为 `scene:xxx`。 */
  qrContent: string;
  qrType: BindQrType | string;
  targetId: string;
  orgId: string;
  expireAt: string;
  maxUse: number;
  needAudit: boolean;
  /** 落地页地址是否已配置（false 时只能手工输入 scene）。 */
  configReady: boolean;
  notice: string | null;
}

export interface BindQrVerifyResult {
  scene: string;
  qrType: string;
  targetId: string;
  targetName: string | null;
  orgId: string;
  orgName: string | null;
  bindRoleCode: string | null;
  needAudit: boolean;
  expireAt: string;
  maxUse: number;
  usedCount: number;
  usable: boolean;
  reason: string | null;
  /** 微信小程序凭据是否已配置（占位值时 false）。 */
  weChatReady: boolean;
}

export interface WxBindRequest {
  scene?: string;
  /** 小程序 wx.login 的 code。 */
  code: string;
}

export interface WxBindResult {
  bound: boolean;
  thirdType: string;
  openidMask: string;
  scene: string | null;
  orgId: string;
  boundAt: string;
}

export interface WxLoginRequest {
  code: string;
}

export interface WxLoginResult {
  token: string;
  tokenType: string;
  expiresInMinutes: number;
  profile: UserProfile;
  channel: string;
}

export interface WxBoundResult {
  bound: boolean;
  thirdType: string;
  openidMask: string | null;
  boundAt: string | null;
  appIdConfigured: boolean;
}

/** 未登录扫码绑定：wx.login 的 code + getPhoneNumber 的 code（同一会话）。 */
export interface WxBindByPhoneRequest {
  scene?: string;
  loginCode: string;
  phoneCode: string;
}

export interface WxBindByPhoneResult {
  /** 已直接绑定并返回 JWT（前端可直接进首页）。 */
  bound: boolean;
  /** 未匹配在职账号，已生成待审核申请。 */
  pending: boolean;
  applyId: string | null;
  openidMask: string | null;
  orgId: string;
  orgName: string | null;
  scene: string | null;
  boundAt: string | null;
  token: string | null;
  tokenType: string | null;
  expiresInMinutes: number | null;
  profile: UserProfile | null;
  /** 直接展示的下一步提示（含未匹配/待审核原因）。 */
  notice: string | null;
}

export interface BindApplyRow {
  id: string;
  userId: string;
  thirdType: string;
  openidMask: string;
  scene: string | null;
  qrType: string;
  orgId: string;
  orgName: string | null;
  bindRoleCode: string | null;
  bindStatus: 'PENDING' | 'BOUND' | 'UNBOUND' | string;
  remark: string | null;
  realName: string | null;
  userCode: string | null;
  createAt: string;
  auditAt: string | null;
}

export interface BindApplyAuditRequest {
  approve: boolean;
  /** 通过时必填：要关联的系统账号。 */
  userId?: string;
  remark?: string;
}

export interface BindApplyAuditResult {
  id: string;
  bindStatus: string;
  userId: string;
  orgId: string;
  deptId: string;
  auditAt: string;
}

export interface BindUserSearchRow {
  userId: string;
  userCode: string;
  realName: string;
  mobileMask: string;
  deptName: string | null;
  orgId: string;
  status: string | null;
  alreadyBound: boolean;
}

export interface MiniCodeResult {
  scene: string;
  page: string;
  contentType: string;
  /** PNG 的 base64（不含 data: 前缀）。 */
  base64: string;
}

/**
 * 扫码绑定三件套：生成二维码 → 扫码核验 → 微信 openid 绑定/免登。
 * 微信凭据未配置（`待补充微信小程序appid/secret`）时后端返回 42200，前端据此隐藏微信绑定入口。
 */
export function createBindApi(http: HttpClient) {
  return {
    /** 生成绑定二维码（需 sys:org:manage 或 sys:user:manage）。 */
    createQr(body: BindQrCreateRequest = {}): Promise<BindQrCreateResult> {
      return http.request<BindQrCreateResult>({ path: '/auth/bind/qrcode', method: 'POST', body });
    },

    /** 核验二维码（匿名可读，落地页用）。 */
    verifyQr(scene: string): Promise<BindQrVerifyResult> {
      return http.request<BindQrVerifyResult>({ path: `/auth/bind/qrcode/${encodeURIComponent(scene)}` });
    },

    /** 已登录账号绑定微信（传 wx.login 的 code）。 */
    bindWeChat(body: WxBindRequest): Promise<WxBindResult> {
      return http.request<WxBindResult>({ path: '/auth/wx/bind', method: 'POST', body });
    },

    /** 微信免登：openid 已绑定则直接换 JWT。 */
    loginByWeChat(body: WxLoginRequest): Promise<WxLoginResult> {
      return http.request<WxLoginResult>({ path: '/auth/wx/login', method: 'POST', body });
    },

    /** 当前账号绑定状态。 */
    bound(): Promise<WxBoundResult> {
      return http.request<WxBoundResult>({ path: '/auth/wx/bound' });
    },

    /** 解绑微信。 */
    unbind(): Promise<{ unbound: number }> {
      return http.request<{ unbound: number }>({ path: '/auth/wx/bind', method: 'DELETE' });
    },

    /**
     * 未登录扫码绑定：手机号命中在职账号 → 直接返回 JWT；未命中 → 生成待审核申请。
     * 需先 `verifyQr(scene)` 取到有效 scene（无 scene 时未命中无法落申请）。
     */
    bindByPhone(body: WxBindByPhoneRequest): Promise<WxBindByPhoneResult> {
      return http.request<WxBindByPhoneResult>({ path: '/auth/wx/bind-by-phone', method: 'POST', body });
    },

    /** 绑定申请列表（默认 PENDING，需 sys:user:manage / sys:org:manage）。 */
    applyList(query: { status?: string; orgId?: string; limit?: number } = {}): Promise<BindApplyRow[]> {
      return http.request<BindApplyRow[]>({
        path: '/sys/bind/apply',
        query: toQuery({ status: query.status, orgId: query.orgId, limit: query.limit })
      });
    },

    /** 审核绑定申请：通过需指定 userId。 */
    auditApply(id: string, body: BindApplyAuditRequest): Promise<BindApplyAuditResult> {
      return http.request<BindApplyAuditResult>({
        path: `/sys/bind/apply/${encodeURIComponent(id)}/audit`,
        method: 'POST',
        body
      });
    },

    /** 审核时定位账号：工号/姓名/手机号模糊。 */
    searchUsers(keyword: string): Promise<BindUserSearchRow[]> {
      return http.request<BindUserSearchRow[]>({
        path: '/sys/bind/user-search',
        query: toQuery({ keyword })
      });
    },

    /** 生成小程序码（wxacode.getUnlimited，scene 直传绑定 scene）。 */
    miniCode(scene: string): Promise<MiniCodeResult> {
      return http.request<MiniCodeResult>({
        path: `/sys/bind/qrcode/${encodeURIComponent(scene)}/minicode`
      });
    }
  };
}

export type BindApi = ReturnType<typeof createBindApi>;
